import { Outlet } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import { useMemo } from 'react';
import useSchools from '../hooks/useSchools';
import useDataIngestionWorkflowStatus from '../hooks/useDataIngestionWorkflowStatus';
import useUserDetails from '../hooks/useUserDetails';
import Loader from '../reusable/Loader';
import PageHeader from '../reusable/PageHeader';
import { NAVIGATION_LINKS_DATA, XQ_SUPPORT_EMAIL, USER_ROLES } from '../../constants';
import SelectDropdown from '../reusable/SelectDropdown';
import useUploadProgress from '../hooks/useUploadProgress';
import { ErrorBoundary } from 'react-error-boundary';
import DataQcEmptyStateImage from '../assets/data-qc-empty-state-image.svg';
import GenericErrorCard from '../reusable/GenericErrorCard';
import CustomButton from '../reusable/CustomButton';
import DataQcUnavailable from './DataQcUnavailable';
import ExternalSchoolTag from '../reusable/ExternalSchoolTag';

import './DataQcLayout.css';

const pageDescription = "Review uploaded district data and make sure it's accurate.";
const allSchoolsValue = 'All schools';

function DataQcLayout() {
  const {
    schools: { isPending: isSchoolsDataPending, error: schoolsError, data: schools },
    dataQcSchool,
    updateDataQcSchool,
  } = useSchools();
  const {
    isLoading: isWorkflowLoading,
    error: workflowError,
    isDataQcAvailable,
    workflowStatus,
    updateWorkflowStatusFromTransition,
  } = useDataIngestionWorkflowStatus();
  const {
    uploadProgressSummary: { isPending: isUploadProgressPending, error: uploadProgressError, data: uploadProgress },
  } = useUploadProgress();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();

  const shouldShowActionButtons = useMemo(() => {
    if (!userDetails) {
      return false;
    }

    const isInternalDataQcComplete = workflowStatus?.current_state === 'INTERNAL_DATA_QC_COMPLETE';
    const isExtractionComplete = workflowStatus?.current_state === 'EXTRACTION_COMPLETE';

    if (!isInternalDataQcComplete && !isExtractionComplete) {
      return false;
    }

    if (isInternalDataQcComplete) {
      return true;
    }

    // Only apply role-based rejection logic when in EXTRACTION_COMPLETE state with active rejections
    const hasRejections =
      workflowStatus?.workflow_metadata?.district_data_qc_rejection &&
      workflowStatus.workflow_metadata.district_data_qc_rejection.length > 0;

    if (!hasRejections) {
      return true;
    }

    const rejections = workflowStatus?.workflow_metadata?.district_data_qc_rejection;
    if (!rejections || rejections.length === 0) {
      return true;
    }

    const latestRejection = rejections[rejections.length - 1];
    const rejectedByRole = latestRejection.district_data_qc_rejected_by_role;
    const currentUserRole = userDetails.role;

    // If XQ admin rejected, hide buttons for both XQ admin and district data lead
    if (rejectedByRole === USER_ROLES.XQ_ADMIN) {
      return false;
    }

    // If district data lead rejected, hide buttons only for district data lead, XQ admin can still see them
    if (rejectedByRole === USER_ROLES.DISTRICT_DATA_LEAD) {
      return currentUserRole === USER_ROLES.XQ_ADMIN;
    }

    return false;
  }, [userDetails, workflowStatus]);

  if (isUploadProgressPending || isWorkflowLoading) return <Loader />;
  if (uploadProgressError) return <p className="center-text">Error occured while fetching upload progress data!</p>;
  if (workflowError) return <p className="center-text">Error occurred while fetching workflow status!</p>;

  if (!isDataQcAvailable) {
    return (
      <>
        <PageHeader title={NAVIGATION_LINKS_DATA.DATA_QC.label} pageDescription={pageDescription} />
        <DataQcUnavailable />
      </>
    );
  }

  const handleChange = (event: SelectChangeEvent) => {
    const { value } = event.target;

    if (value === allSchoolsValue) updateDataQcSchool('');
    else updateDataQcSchool(value);
  };

  const getDropdownItems = () => {
    if (!schools) return [];

    let items = [{ label: allSchoolsValue, value: allSchoolsValue }];
    schools.forEach((school) => items.push({ label: school.name, value: school.id }));

    return items;
  };

  const getExternalSchools = () => {
    if (!schools) return new Set<string>();
    return new Set(schools.filter((school) => !school.is_primary_on_eoa).map((school) => school.name));
  };

  return (
    <>
      <PageHeader title={NAVIGATION_LINKS_DATA.DATA_QC.label} pageDescription={pageDescription} />
      <ErrorBoundary
        fallback={
          <div className="main-content">
            <GenericErrorCard
              title="Something went wrong while loading the data"
              text={
                <>
                  Please try refreshing the page. If the issue continues,{' '}
                  <a href={`mailto:${XQ_SUPPORT_EMAIL}`} className="link-color">
                    contact support
                  </a>{' '}
                  for help.
                </>
              }
              imgSrc={DataQcEmptyStateImage}
              button={
                <CustomButton
                  variant="primary"
                  buttonText="Refresh page"
                  buttonAction={() => window.location.reload()}
                />
              }
            />
          </div>
        }
      >
        <div className="select-school-bar">
          {isSchoolsDataPending ? <Loader /> : null}
          {schoolsError ? <p>Missing schools data!</p> : null}
          {schools && (
            <>
              <span>Display data for</span>
              <div className="school-select-with-tag">
                <SelectDropdown
                  label="School"
                  selectedValue={dataQcSchool ? dataQcSchool : 'All schools'}
                  handleChange={handleChange}
                  items={getDropdownItems()}
                  isClearable={false}
                  externalSchools={getExternalSchools()}
                />
                {dataQcSchool && schools.find((s) => s.id === dataQcSchool)?.is_primary_on_eoa === false && (
                  <ExternalSchoolTag variant="external" showTooltip={true} />
                )}
              </div>
            </>
          )}
        </div>
        <Outlet
          context={{
            uploadProgress,
            schools,
            dataQcSchool,
            shouldShowActionButtons,
            updateWorkflowStatusFromTransition,
            workflowStatus,
          }}
        />
      </ErrorBoundary>
    </>
  );
}

export default DataQcLayout;
